import { useSideEffectOnMount } from "@web-monorepo/hooks";
import * as logClient from "@classdojo/log-client";
import { ProductEvent } from "@classdojo/log-client/lib/client/client";
import { identity } from "es-toolkit/compat";
import { useEffect } from "react";
import { navigate } from "src/utils/reactRouterNavigate";
import Cookies, { type CookieSetOptions } from "universal-cookie";
import * as Android from "./androidCommands";
import * as Ios from "./iosCommands";
import { mountPoint } from "src/pods/realtime/util/role";
import { userAgentPlatform } from "src/utils/userAgentPlatform";
import { getToolkitStoreState, toolkitActions, useToolkitStore } from "src/state/toolkitStore";
import { ToolkitNativeAction } from "src/state/toolkitNativeBridge";

const ANDROID_WEBVIEW_INIT_ACTION = "android/initWebview";
const IOS_WEBVIEW_INIT_ACTION = "ios/initWebview";
const NAVIGATE_ACTION = "navigate";

const detectedPlatform = typeof window === "undefined" ? null : userAgentPlatform();
let didInitializeNativeBridge = false;

type NativeCommandModule = {
  init: () => void;
  showNativeNav: (shouldShow: boolean) => void;
  closeWebview: () => void;
  openSalesPage: () => void;
  openPointsSheet: () => void;
  logEvent: (event: ProductEvent) => void;
};

type ToolkitWebviewRuntimeDependencies = {
  android: NativeCommandModule;
  ios: NativeCommandModule;
  navigate: (route: string) => void;
};

const defaultWebviewRuntimeDependencies: ToolkitWebviewRuntimeDependencies = {
  android: Android,
  ios: Ios,
  navigate,
};

let toolkitWebviewRuntimeDependencies = defaultWebviewRuntimeDependencies;

function isMobileWebviewContext() {
  return window.location.pathname.includes("beyondToolkit") || mountPoint === "mobile";
}

function getPlatform() {
  return getToolkitStoreState().webviewPlatform ?? detectedPlatform;
}

function bootstrapSessionCookieFromHash() {
  const cookies = new Cookies();
  // universal-cookie v2 only JSON-parsed values that looked like JSON; v8 always
  // tries, so opt out to keep reading the session id as a raw string.
  if (cookies.get("dojo_login.sid", { doNotParse: true })) {
    return;
  }

  const session = window.location.hash.replace(/^#/, "");
  if (session.trim().length === 0) {
    return;
  }

  const domain = window.location.hostname.endsWith(".com") ? ".classdojo.com" : ".classdojo.test";
  // universal-cookie forwards unrecognised options straight to `cookie.serialize`,
  // but its CookieSetOptions type no longer declares `encode`. The session value
  // arrives already encoded from the native app and must be written verbatim.
  const options: CookieSetOptions & { encode?: (value: string) => string } = {
    domain,
    path: "/",
    expires: new Date("2031-12-31"),
    encode: identity,
  };
  cookies.set("dojo_login.sid", session, options);
}

function getBuildNumber(action: ToolkitNativeAction) {
  if (action.type === IOS_WEBVIEW_INIT_ACTION) {
    const meta = action.meta;
    if (meta && typeof meta === "object" && typeof (meta as Record<string, unknown>).buildNumber === "number") {
      return (meta as { buildNumber: number }).buildNumber;
    }
  }

  return typeof action.buildNumber === "number" ? action.buildNumber : null;
}

function navigateToRoute(route: string) {
  try {
    toolkitWebviewRuntimeDependencies.navigate(route);
  } catch (ex: unknown) {
    throw new Error(`navigateToRoute: ${ex instanceof Error ? ex.message : String(ex)}`, { cause: ex });
  }
}

export function setToolkitWebviewRuntimeDependenciesForTest(overrides: Partial<ToolkitWebviewRuntimeDependencies>) {
  toolkitWebviewRuntimeDependencies = {
    android: overrides.android ?? toolkitWebviewRuntimeDependencies.android,
    ios: overrides.ios ?? toolkitWebviewRuntimeDependencies.ios,
    navigate: overrides.navigate ?? toolkitWebviewRuntimeDependencies.navigate,
  };
}

export function handleToolkitWebviewAction(action: ToolkitNativeAction) {
  if (action.type === ANDROID_WEBVIEW_INIT_ACTION || action.type === IOS_WEBVIEW_INIT_ACTION) {
    const platform = action.type === IOS_WEBVIEW_INIT_ACTION ? "ios" : "android";
    toolkitActions.setWebviewPlatform(platform, getBuildNumber(action));
    return true;
  }

  if (action.type === NAVIGATE_ACTION && typeof action.payload === "string") {
    navigateToRoute(action.payload);
    return true;
  }

  return false;
}

export function useToolkitWebviewRuntime() {
  const { isInteractable, webviewPlatform } = useToolkitStore();

  useSideEffectOnMount(() => {
    if (isMobileWebviewContext()) {
      bootstrapSessionCookieFromHash();
      return;
    }

    toolkitActions.setWebviewPlatform("web");
  });

  useEffect(() => {
    if (!isMobileWebviewContext()) {
      return;
    }

    if (!isInteractable || didInitializeNativeBridge) {
      return;
    }

    if (webviewPlatform === "ios") {
      didInitializeNativeBridge = true;
      toolkitWebviewRuntimeDependencies.ios.init();
      return;
    }

    if (webviewPlatform === "android") {
      didInitializeNativeBridge = true;
      toolkitWebviewRuntimeDependencies.android.init();
    }
  }, [isInteractable, webviewPlatform]);
}

export function showNativeNav(shouldShow: boolean) {
  const platform = getPlatform();
  if (platform === "ios") {
    toolkitWebviewRuntimeDependencies.ios.showNativeNav(shouldShow);
  } else if (platform === "android") {
    toolkitWebviewRuntimeDependencies.android.showNativeNav(shouldShow);
  }
}

export function closeWebview() {
  const platform = getPlatform();
  if (platform === "ios") {
    toolkitWebviewRuntimeDependencies.ios.closeWebview();
  } else if (platform === "android") {
    toolkitWebviewRuntimeDependencies.android.closeWebview();
  }
}

export function openSalesPage() {
  const platform = getPlatform();
  if (platform === "ios") {
    toolkitWebviewRuntimeDependencies.ios.openSalesPage();
  } else if (platform === "android") {
    toolkitWebviewRuntimeDependencies.android.openSalesPage();
  }
}

export function openPointsSheet() {
  const platform = getPlatform();
  if (platform === "ios") {
    toolkitWebviewRuntimeDependencies.ios.openPointsSheet();
  } else if (platform === "android") {
    toolkitWebviewRuntimeDependencies.android.openPointsSheet();
  }
}

export function logEvent(event: ProductEvent, role: string | null = null) {
  const platform = getPlatform();
  //Platform prefix is added during ios and android native event logging
  if (platform === "ios") {
    //Role prefix is NOT added during iOS native event logging, so add it here
    if (role) event.eventName = `${role}.${event.eventName}`;
    toolkitWebviewRuntimeDependencies.ios.logEvent(event);
  } else if (platform === "android") {
    //Role prefix is added during android native event logging, so skip it here to avoid "parent.parent" issue
    toolkitWebviewRuntimeDependencies.android.logEvent(event);
  } else {
    //Need to add web platform prefix and role here
    if (role) event.eventName = `${role}.${event.eventName}`;
    return logClient.logEvent({ ...event, eventName: `web.${event.eventName}` });
  }
}

export function resetToolkitWebviewRuntimeForTest() {
  didInitializeNativeBridge = false;
  toolkitWebviewRuntimeDependencies = defaultWebviewRuntimeDependencies;
}
